import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// ─── Constants ────────────────────────────────────────────────────────────────
const STAGES = [
  { id: 'confirmed',            label: 'Onaylandı',          color: '#6366f1', bg: '#eef2ff', icon: '✅' },
  { id: 'glass_cutting',        label: 'Cam Kesim',          color: '#f59e0b', bg: '#fffbeb', icon: '🔷' },
  { id: 'profile_preparation',  label: 'Profil Hazırlık',    color: '#ec4899', bg: '#fdf2f8', icon: '⚙️'  },
  { id: 'assembly',             label: 'Montaj / Paketleme', color: '#10b981', bg: '#ecfdf5', icon: '📦' },
  { id: 'ready_for_install',    label: 'Sevkiyata Hazır',    color: '#3b82f6', bg: '#eff6ff', icon: '🚚' },
];

const GLASS_TYPES = ['Şeffaf', 'Buzlu', 'Model Cam', 'Bronz', 'Gri', 'Siyah'];
const PROFILE_COLORS = ['Krom', 'Mat Krom', 'Siyah Mat', 'Altın', 'Bronz', 'Beyaz'];
const CABIN_TYPES = [
  'Pivot Kapı', 'Sürgülü Kapı', 'Köşe Kabin', 'Yarım Daire Kabin',
  'Walk-In Duş', 'Asimetrik Kabin', 'Özel Ölçü',
];

const CITIES = ['Adana','Ankara','Antalya','Bursa','Eskişehir','Gaziantep','İstanbul','İzmir','Kayseri','Konya','Mersin','Samsun','Trabzon','Diğer'];

// ─── Helpers ─────────────────────────────────────────────────────────────────
const api = (path, opts = {}) =>
  axios({ url: `/api${path}`, headers: { Authorization: `Bearer ${localStorage.getItem('crm_token')}` }, ...opts });

const fmt   = d => d ? new Date(d).toLocaleDateString('tr-TR') : '-';
const money = n => n ? Number(n).toLocaleString('tr-TR') + ' ₺' : '-';

const emptyItem = () => ({
  _key: Date.now() + Math.random(),
  type: 'Pivot Kapı', width: '', height: '', glass_type: 'Şeffaf',
  profile_color: 'Krom', quantity: 1, unit_price: '',
});

const emptyForm = () => ({
  first_name: '', last_name: '', phone: '', city: '', address: '',
  installation_address: '', installation_date: '', estimated_completion: '',
  discount_amount: 0, vat_amount: 0, installation_fee: 0,
  notes: '', production_notes: '',
  items: [emptyItem()],
});

// ─── New Order Modal ──────────────────────────────────────────────────────────
function NewOrderModal({ onClose, onCreated }) {
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Derived totals
  const subtotal = form.items.reduce((s, it) => s + (Number(it.unit_price) * Number(it.quantity || 1)), 0);
  const total = subtotal - Number(form.discount_amount) + Number(form.vat_amount) + Number(form.installation_fee);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const setItem = (key, idx, val) => setForm(f => {
    const items = [...f.items];
    items[idx] = { ...items[idx], [key]: val };
    return { ...f, items };
  });
  const addItem = () => setForm(f => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeItem = idx => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const submit = async () => {
    if (!form.first_name || !form.phone) return setError('Ad ve telefon zorunludur.');
    if (form.items.some(it => !it.width || !it.height || !it.unit_price))
      return setError('Tüm ürün satırlarında genişlik, yükseklik ve birim fiyat giriniz.');

    setSaving(true); setError('');
    try {
      const res = await api('/orders/direct', {
        method: 'post',
        data: { ...form, subtotal, total_amount: total,
          items: form.items.map(({ _key, ...rest }) => rest) },
      });
      onCreated(res.data);
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'Kayıt hatası');
    } finally { setSaving(false); }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={{ ...modalBox, width: '780px', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#0f172a' }}>🏭 Yeni Üretim Siparişi</h2>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>

        {error && <div style={errorBox}>{error}</div>}

        {/* ── Müşteri Bilgileri ── */}
        <Section title="👤 Müşteri Bilgileri">
          <div style={grid2}>
            <Field label="Ad *"><input style={inp} value={form.first_name} onChange={e => set('first_name', e.target.value)} placeholder="Ad" /></Field>
            <Field label="Soyad"><input style={inp} value={form.last_name} onChange={e => set('last_name', e.target.value)} placeholder="Soyad" /></Field>
            <Field label="Telefon *"><input style={inp} value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="05xx xxx xx xx" /></Field>
            <Field label="Şehir">
              <select style={inp} value={form.city} onChange={e => set('city', e.target.value)}>
                <option value="">Seçin</option>
                {CITIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Müşteri Adresi">
            <input style={inp} value={form.address} onChange={e => set('address', e.target.value)} placeholder="Tam adres" />
          </Field>
        </Section>

        {/* ── Ürün Kalemleri ── */}
        <Section title="📐 Ürün Kalemleri">
          {form.items.map((item, idx) => (
            <div key={item._key} style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', marginBottom: '12px', border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span style={{ fontWeight: 700, fontSize: '13px', color: 'var(--tertiary)' }}>Kalem {idx + 1}</span>
                {form.items.length > 1 && (
                  <button onClick={() => removeItem(idx)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '18px' }}>×</button>
                )}
              </div>
              <div style={grid3}>
                <Field label="Kabin Tipi">
                  <select style={inp} value={item.type} onChange={e => setItem('type', idx, e.target.value)}>
                    {CABIN_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Genişlik (cm) *">
                  <input style={inp} type="number" value={item.width} onChange={e => setItem('width', idx, e.target.value)} placeholder="örn: 90" />
                </Field>
                <Field label="Yükseklik (cm) *">
                  <input style={inp} type="number" value={item.height} onChange={e => setItem('height', idx, e.target.value)} placeholder="örn: 200" />
                </Field>
                <Field label="Cam Tipi">
                  <select style={inp} value={item.glass_type} onChange={e => setItem('glass_type', idx, e.target.value)}>
                    {GLASS_TYPES.map(g => <option key={g}>{g}</option>)}
                  </select>
                </Field>
                <Field label="Profil Rengi">
                  <select style={inp} value={item.profile_color} onChange={e => setItem('profile_color', idx, e.target.value)}>
                    {PROFILE_COLORS.map(p => <option key={p}>{p}</option>)}
                  </select>
                </Field>
                <Field label="Adet">
                  <input style={inp} type="number" min="1" value={item.quantity} onChange={e => setItem('quantity', idx, e.target.value)} />
                </Field>
                <Field label="Birim Fiyat (₺) *">
                  <input style={inp} type="number" value={item.unit_price} onChange={e => setItem('unit_price', idx, e.target.value)} placeholder="0" />
                </Field>
                <Field label="Satır Toplamı">
                  <input style={{ ...inp, background: '#f1f5f9', color: '#475569' }} readOnly
                    value={item.unit_price ? (Number(item.unit_price) * Number(item.quantity || 1)).toLocaleString('tr-TR') + ' ₺' : '-'} />
                </Field>
              </div>
            </div>
          ))}
          <button onClick={addItem} style={{ ...btnOutline, width: '100%' }}>+ Ürün Kalemi Ekle</button>
        </Section>

        {/* ── Fiyatlandırma ── */}
        <Section title="💰 Fiyatlandırma">
          <div style={grid3}>
            <Field label="Ara Toplam">
              <input style={{ ...inp, background: '#f1f5f9', color: '#475569' }} readOnly value={subtotal.toLocaleString('tr-TR') + ' ₺'} />
            </Field>
            <Field label="İndirim (₺)">
              <input style={inp} type="number" value={form.discount_amount} onChange={e => set('discount_amount', e.target.value)} placeholder="0" />
            </Field>
            <Field label="KDV (₺)">
              <input style={inp} type="number" value={form.vat_amount} onChange={e => set('vat_amount', e.target.value)} placeholder="0" />
            </Field>
            <Field label="Montaj Ücreti (₺)">
              <input style={inp} type="number" value={form.installation_fee} onChange={e => set('installation_fee', e.target.value)} placeholder="0" />
            </Field>
            <Field label="Genel Toplam">
              <input style={{ ...inp, background: '#ecfdf5', color: '#059669', fontWeight: 700, fontSize: '15px' }} readOnly value={total.toLocaleString('tr-TR') + ' ₺'} />
            </Field>
          </div>
        </Section>

        {/* ── Teslimat ── */}
        <Section title="🚚 Teslimat Bilgileri">
          <Field label="Montaj Adresi">
            <input style={inp} value={form.installation_address} onChange={e => set('installation_address', e.target.value)} placeholder="Montaj yapılacak adres (farklıysa)" />
          </Field>
          <div style={grid2}>
            <Field label="Montaj Tarihi">
              <input style={inp} type="date" value={form.installation_date} onChange={e => set('installation_date', e.target.value)} />
            </Field>
            <Field label="Tahmini Tamamlanma">
              <input style={inp} type="date" value={form.estimated_completion} onChange={e => set('estimated_completion', e.target.value)} />
            </Field>
          </div>
        </Section>

        {/* ── Notlar ── */}
        <Section title="📝 Notlar">
          <div style={grid2}>
            <Field label="Müşteri Notu">
              <textarea style={{ ...inp, resize: 'vertical' }} rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Müşteriye özel notlar..." />
            </Field>
            <Field label="Üretim Notu">
              <textarea style={{ ...inp, resize: 'vertical' }} rows={3} value={form.production_notes} onChange={e => set('production_notes', e.target.value)} placeholder="Üretim ekibine talimatlar..." />
            </Field>
          </div>
        </Section>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', paddingTop: '8px' }}>
          <button onClick={onClose} style={btnSecondary}>İptal</button>
          <button onClick={submit} disabled={saving} style={{ ...btnPrimary, opacity: saving ? .7 : 1 }}>
            {saving ? 'Kaydediliyor…' : '✅ Siparişi Oluştur'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Detail / Edit Modal ──────────────────────────────────────────────────────
function DetailModal({ order, onClose, onMove, onSaved, onDelete }) {
  const [note, setNote] = useState(order.production_notes || '');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const idx = STAGES.findIndex(s => s.id === order.status);
  const stage = STAGES[idx] || STAGES[0];

  const save = async () => {
    setSaving(true);
    try {
      await api(`/orders/${order.id}`, { method: 'put', data: { production_notes: note } });
      onSaved(order.id, note);
    } catch { alert('Kaydedilemedi'); }
    setSaving(false);
  };

  const doDelete = async () => {
    try {
      await api(`/orders/${order.id}`, { method: 'delete' });
      onDelete(order.id);
      onClose();
    } catch { alert('Silinemedi'); }
  };

  const items = (() => { try { return JSON.parse(order.items || '[]'); } catch { return []; } })();

  return (
    <div style={overlay} onClick={onClose}>
      <div style={{ ...modalBox, width: '600px', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div>
            <span style={{ fontSize: '11px', fontWeight: 700, color: stage.color }}>{order.order_number}</span>
            <h2 style={{ margin: '4px 0 0', fontSize: '20px', fontWeight: 800, color: '#0f172a' }}>
              {order.customer_first_name} {order.customer_last_name}
            </h2>
          </div>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>

        {/* Stage pipeline */}
        <div style={{ display: 'flex', gap: '3px', marginBottom: '6px' }}>
          {STAGES.map((s, i) => (
            <div key={s.id} title={s.label} style={{ flex: 1, height: '7px', borderRadius: '4px', background: i <= idx ? s.color : '#f1f5f9', cursor: 'pointer', transition: 'background .2s' }}
              onClick={() => { onMove(order.id, s.id); }} />
          ))}
        </div>
        <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '20px', textAlign: 'center' }}>
          {stage.icon} <strong>{stage.label}</strong> — Aşama üzerine tıklayarak da değiştirebilirsiniz
        </div>

        {/* Info grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px' }}>
          {[
            ['📍 Şehir', order.city || '-'],
            ['💰 Toplam', money(order.total_amount)],
            ['📅 Başlangıç', fmt(order.production_start_date)],
            ['📅 Montaj', fmt(order.installation_date)],
            ['📅 Tah. Bitiş', fmt(order.estimated_completion)],
            ['📞 Telefon', order.customer_phone || '-'],
          ].map(([l, v]) => (
            <div key={l} style={{ background: '#f8fafc', padding: '10px 14px', borderRadius: '10px' }}>
              <div style={{ fontSize: '11px', color: '#94a3b8' }}>{l}</div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b', marginTop: '2px' }}>{v}</div>
            </div>
          ))}
        </div>

        {/* Products */}
        {items.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', marginBottom: '8px' }}>📐 ÜRÜN KALEMLERİ</div>
            <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['Tip', 'Ölçü', 'Cam', 'Profil', 'Adet', 'Fiyat'].map(h => (
                      <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={i} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px 10px', color: '#1e293b', fontWeight: 500 }}>{it.type}</td>
                      <td style={{ padding: '8px 10px', color: '#475569' }}>{it.width}×{it.height}</td>
                      <td style={{ padding: '8px 10px', color: '#475569' }}>{it.glass_type}</td>
                      <td style={{ padding: '8px 10px', color: '#475569' }}>{it.profile_color}</td>
                      <td style={{ padding: '8px 10px', color: '#475569' }}>{it.quantity}</td>
                      <td style={{ padding: '8px 10px', color: '#0f172a', fontWeight: 600 }}>{it.unit_price ? Number(it.unit_price).toLocaleString('tr-TR') + ' ₺' : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Notlar */}
        {order.notes && (
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', padding: '12px', marginBottom: '16px' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#92400e', marginBottom: '4px' }}>MÜŞTERİ NOTU</div>
            <div style={{ fontSize: '13px', color: '#78350f' }}>{order.notes}</div>
          </div>
        )}

        <div style={{ marginBottom: '20px' }}>
          <label style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', display: 'block', marginBottom: '6px' }}>📝 Üretim Notu</label>
          <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
            style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px', fontSize: '13px', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }} />
          <button onClick={save} disabled={saving}
            style={{ ...btnPrimary, marginTop: '8px', fontSize: '13px', padding: '8px 18px', opacity: saving ? .7 : 1 }}>
            {saving ? 'Kaydediliyor…' : 'Notu Kaydet'}
          </button>
        </div>

        {/* Stage nav + delete */}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => setConfirmDelete(true)}
            style={{ padding: '8px 16px', border: '1px solid #fecaca', background: '#fff', color: '#ef4444', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>
            🗑 Sil
          </button>
          <div style={{ display: 'flex', gap: '8px' }}>
            {idx > 0 && (
              <button onClick={() => { onMove(order.id, STAGES[idx - 1].id); onClose(); }}
                style={{ ...btnSecondary, fontSize: '13px', padding: '8px 16px' }}>← {STAGES[idx - 1].label}</button>
            )}
            {idx < STAGES.length - 1 && (
              <button onClick={() => { onMove(order.id, STAGES[idx + 1].id); onClose(); }}
                style={{ ...btnPrimary, fontSize: '13px', padding: '8px 16px' }}>{STAGES[idx + 1].label} →</button>
            )}
          </div>
        </div>

        {confirmDelete && (
          <div style={{ marginTop: '16px', background: '#fff5f5', padding: '14px', borderRadius: '10px', border: '1px solid #fecaca' }}>
            <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#7f1d1d' }}>Bu siparişi silmek istediğinize emin misiniz?</p>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => setConfirmDelete(false)} style={btnSecondary}>İptal</button>
              <button onClick={doDelete} style={{ ...btnPrimary, background: '#ef4444' }}>Evet, Sil</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Order Card ───────────────────────────────────────────────────────────────
function OrderCard({ order, onMove, onDetail }) {
  const idx = STAGES.findIndex(s => s.id === order.status);
  const stage = STAGES[idx] || STAGES[0];
  const progress = Math.round(((idx + 1) / STAGES.length) * 100);
  const items = (() => { try { return JSON.parse(order.items || '[]'); } catch { return []; } })();

  return (
    <div style={card} onClick={() => onDetail(order)}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.12)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = card.boxShadow}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
        <span style={{ fontSize: '11px', fontWeight: 700, color: stage.color, background: stage.bg, padding: '2px 8px', borderRadius: '20px' }}>
          {order.order_number}
        </span>
        <span style={{ fontSize: '10px', color: '#94a3b8' }}>{fmt(order.created_at)}</span>
      </div>
      <div style={{ fontWeight: 700, fontSize: '14px', color: '#1e293b', marginBottom: '3px' }}>
        {order.customer_first_name} {order.customer_last_name}
      </div>
      {order.customer_phone && <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>📞 {order.customer_phone}</div>}
      {order.city && <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '6px' }}>📍 {order.city}</div>}
      {items.length > 0 && (
        <div style={{ fontSize: '11px', color: 'var(--tertiary)', marginBottom: '6px', fontWeight: 500 }}>
          📐 {items.map(it => `${it.type} ${it.width}×${it.height}`).join(' · ')}
        </div>
      )}
      {order.total_amount && (
        <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', marginBottom: '8px' }}>
          💰 {money(order.total_amount)}
        </div>
      )}
      {/* Progress */}
      <div style={{ background: '#f1f5f9', borderRadius: '4px', height: '4px', marginBottom: '10px' }}>
        <div style={{ width: `${progress}%`, background: stage.color, borderRadius: '4px', height: '100%', transition: 'width .3s' }} />
      </div>
      {order.production_notes && (
        <div style={{ fontSize: '11px', color: '#64748b', background: '#f8fafc', padding: '4px 8px', borderRadius: '6px', marginBottom: '8px' }}>
          📝 {order.production_notes.slice(0, 60)}{order.production_notes.length > 60 ? '…' : ''}
        </div>
      )}
      <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }} onClick={e => e.stopPropagation()}>
        {idx > 0 && (
          <button style={cardBtnGray} onClick={() => onMove(order.id, STAGES[idx - 1].id)}>← Geri</button>
        )}
        {idx < STAGES.length - 1 && (
          <button style={{ ...cardBtnPrimary, background: stage.color }} onClick={() => onMove(order.id, STAGES[idx + 1].id)}>İlerlet →</button>
        )}
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function Production() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [detailOrder, setDetailOrder] = useState(null);
  const [filterStage, setFilterStage] = useState('all');
  const [search, setSearch] = useState('');

  const fetchOrders = useCallback(async () => {
    try {
      const res = await api('/orders');
      setOrders(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const moveOrder = async (id, status) => {
    try {
      await api(`/orders/${id}`, { method: 'put', data: { status } });
      setOrders(prev => prev.map(o => o.id === id ? { ...o, status } : o));
      if (detailOrder?.id === id) setDetailOrder(p => p ? { ...p, status } : null);
    } catch { alert('Güncellenemedi'); }
  };

  const saveNote = (id, note) =>
    setOrders(prev => prev.map(o => o.id === id ? { ...o, production_notes: note } : o));

  const deleteOrder = id => setOrders(prev => prev.filter(o => o.id !== id));

  const filtered = orders.filter(o => {
    const stageOk = filterStage === 'all' || o.status === filterStage;
    const q = search.toLowerCase();
    const searchOk = !q || [o.customer_first_name, o.customer_last_name, o.order_number, o.customer_phone, o.city]
      .some(v => v?.toLowerCase().includes(q));
    return stageOk && searchOk;
  });

  const totalAmount = orders.reduce((s, o) => s + (Number(o.total_amount) || 0), 0);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '12px', color: 'var(--tertiary)', flexDirection: 'column' }}>
      <div style={{ width: 40, height: 40, border: '3px solid #e0e7ff', borderTopColor: 'var(--tertiary)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      <span style={{ fontWeight: 600 }}>Yükleniyor...</span>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#f8fafc', fontFamily: 'inherit' }}>

      {/* ── Top Bar ── */}
      <div style={{ background: 'white', borderBottom: '1px solid #f1f5f9', padding: '18px 24px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 800, color: '#0f172a' }}>🏭 Üretim Takip Panosu</h1>
            <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#94a3b8' }}>Duşakabin siparişlerini oluşturun ve üretim sürecini yönetin</p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={fetchOrders} style={btnSecondary}>🔄 Yenile</button>
            <button onClick={() => setShowNew(true)} style={btnPrimary}>+ Yeni Sipariş</button>
          </div>
        </div>

        {/* Stat bar */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', overflowX: 'auto', paddingBottom: '2px' }}>
          {[
            { l: 'Toplam', v: orders.length, c: '#6366f1', b: '#eef2ff', i: '📋' },
            { l: 'Toplam Ciro', v: money(totalAmount), c: '#10b981', b: '#ecfdf5', i: '💰' },
            ...STAGES.map(s => ({ l: s.label, v: orders.filter(o => o.status === s.id).length, c: s.color, b: s.bg, i: s.icon })),
          ].map(st => (
            <div key={st.l} style={{ minWidth: '120px', background: st.b, padding: '10px 14px', borderRadius: '10px', flexShrink: 0 }}>
              <div style={{ fontSize: '16px' }}>{st.i}</div>
              <div style={{ fontSize: '18px', fontWeight: 800, color: st.c }}>{st.v}</div>
              <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 500 }}>{st.l}</div>
            </div>
          ))}
        </div>

        {/* Search + Stage filters */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0', paddingBottom: '0' }}>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="🔍 Müşteri adı, sipariş no, telefon..."
            style={{ ...inp, width: '260px', margin: 0, padding: '8px 12px', fontSize: '13px' }}
          />
          <div style={{ display: 'flex', gap: '4px', overflowX: 'auto' }}>
            <TabBtn active={filterStage === 'all'} color="#1e293b" onClick={() => setFilterStage('all')}>
              Tümü ({orders.length})
            </TabBtn>
            {STAGES.map(s => (
              <TabBtn key={s.id} active={filterStage === s.id} color={s.color} onClick={() => setFilterStage(s.id)}>
                {s.icon} {s.label} ({orders.filter(o => o.status === s.id).length})
              </TabBtn>
            ))}
          </div>
        </div>
      </div>

      {/* ── Kanban ── */}
      <div style={{ flex: 1, display: 'flex', gap: '12px', padding: '16px 24px 24px', overflowX: 'auto', overflowY: 'hidden' }}>
        {STAGES.map(stage => {
          const stageOrders = filtered.filter(o => o.status === stage.id);
          return (
            <div key={stage.id} style={{ minWidth: '280px', maxWidth: '280px', display: 'flex', flexDirection: 'column', flex: '0 0 280px' }}>
              <div style={{ padding: '11px 14px', borderRadius: '12px 12px 0 0', background: stage.color, color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 700, fontSize: '13px' }}>{stage.icon} {stage.label}</span>
                <span style={{ background: 'rgba(255,255,255,0.25)', padding: '2px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 700 }}>
                  {stageOrders.length}
                </span>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '10px 8px', background: stage.bg, borderRadius: '0 0 12px 12px', minHeight: '280px', maxHeight: 'calc(100vh - 340px)' }}>
                {stageOrders.length === 0
                  ? <div style={{ textAlign: 'center', padding: '40px 12px', color: '#94a3b8', fontSize: '13px' }}>
                      <div style={{ fontSize: '26px', opacity: .4, marginBottom: '8px' }}>{stage.icon}</div>
                      Kayıt yok
                    </div>
                  : stageOrders.map(o => (
                      <OrderCard key={o.id} order={o} onMove={moveOrder} onDetail={setDetailOrder} />
                    ))
                }
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty */}
      {orders.length === 0 && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <div style={{ fontSize: '56px' }}>🏭</div>
          <div style={{ fontSize: '18px', fontWeight: 700, color: '#64748b', margin: '12px 0 6px' }}>Henüz üretim siparişi yok</div>
          <div style={{ fontSize: '14px', color: '#94a3b8' }}>Sağ üstteki "Yeni Sipariş" butonuyla başlayın.</div>
        </div>
      )}

      {showNew && <NewOrderModal onClose={() => setShowNew(false)} onCreated={fetchOrders} />}
      {detailOrder && (
        <DetailModal
          order={detailOrder}
          onClose={() => setDetailOrder(null)}
          onMove={moveOrder}
          onSaved={saveNote}
          onDelete={deleteOrder}
        />
      )}
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function Section({ title, children }) {
  return (
    <div style={{ marginBottom: '20px' }}>
      <div style={{ fontSize: '12px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '12px' }}>{title}</div>
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <label style={{ fontSize: '12px', fontWeight: 600, color: '#64748b' }}>{label}</label>
      {children}
    </div>
  );
}
function TabBtn({ active, color, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: '7px 14px', borderRadius: '8px 8px 0 0', border: 'none', fontWeight: 600, fontSize: '12px',
      cursor: 'pointer', whiteSpace: 'nowrap', background: active ? color : 'transparent', color: active ? 'white' : '#64748b',
    }}>{children}</button>
  );
}

// ─── Shared Styles ────────────────────────────────────────────────────────────
const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '20px' };
const modalBox = { background: 'white', borderRadius: '20px', padding: '28px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' };
const closeBtn = { background: 'none', border: 'none', fontSize: '26px', cursor: 'pointer', color: '#64748b', lineHeight: 1 };
const inp = { border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px 12px', fontSize: '14px', fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box', color: '#1e293b' };
const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' };
const grid3 = { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' };
const btnPrimary = { padding: '10px 22px', background: '#6366f1', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 700, fontSize: '14px' };
const btnSecondary = { padding: '10px 22px', background: 'white', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: '10px', cursor: 'pointer', fontWeight: 600, fontSize: '14px' };
const btnOutline = { padding: '10px', background: 'white', color: '#6366f1', border: '1px dashed #a5b4fc', borderRadius: '10px', cursor: 'pointer', fontWeight: 600, fontSize: '13px' };
const errorBox = { background: '#fff5f5', border: '1px solid #fecaca', color: '#dc2626', padding: '12px 16px', borderRadius: '10px', fontSize: '13px', marginBottom: '16px' };
const card = { background: 'white', borderRadius: '12px', padding: '14px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', cursor: 'pointer', border: '1px solid #f1f5f9', transition: 'box-shadow .15s', marginBottom: '10px' };
const cardBtnGray = { fontSize: '11px', padding: '4px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', background: 'white', color: '#64748b', cursor: 'pointer' };
const cardBtnPrimary = { fontSize: '11px', padding: '4px 10px', borderRadius: '6px', border: 'none', color: 'white', cursor: 'pointer', fontWeight: 600 };
